// Per-field AI help — the ONE thread component (design D2). Sheet and inline
// hosts differ only in chrome; the log, starter chips, fill-or-explain
// rendering, and composer all live here so both hosts behave identically.
//
// Messages route fill-first (D1, the adopted v1 transport — a normal
// `/question/` turn, one-field `open_fields`): a reply that filled the scoped
// field carries a receipt (name-only for a sensitive OR withheld field,
// mirroring `FillReceipts`); anything else is the plain-language explanation.
// A turn can fill more than the scoped field (auto mode already persisted
// every fill server-side) — every receipt the turn returned renders, the
// scoped one as the primary fill line and any others as compact secondary
// lines, so a fill is never invisible (adversarial-review M7). Starter chips
// seed an empty thread and disappear once it has content (D4), or are hidden
// entirely while the field is locked. Failures render the same retryable
// bubble as the main conversation (D5), scoped to this thread; an unretryable
// partial/full extraction failure gets the same quiet `FillNotice` the main
// conversation shows (adversarial-review M5).

import { RefObject, useEffect, useRef, useState } from 'react';
import MessageContent from '../Chat/Body/Messages/Message/MessageContent';
import { RETRYABLE_FILL_NOTICE, SLOW_TURN_CAPTION } from './constants';
import styles from './ConversationalForms.module.css';
import FillNotice from './FillNotice';
import { AlertIcon } from './icons';
import { CfField } from './types';

/** One receipt in a help-thread message — mirrors `FillReceipt`'s shape so a
 *  sensitive or withheld field names itself without echoing its value into
 *  the thread. */
export type HelpFill = {
    fieldId: string;
    label: string;
    display: string;
    sensitive: boolean;
    /** This is the field the thread is scoped to — renders as the primary
     *  "this filled the field you asked about" line. A same-turn fill for a
     *  DIFFERENT field renders as a secondary line instead (M7): the write
     *  already happened server-side, so it is never dropped silently, but it
     *  doesn't read as this thread's own answer. */
    scoped: boolean;
};

export type HelpMessage = {
    role: 'user' | 'bot';
    text: string;
    /** Every receipt this reply's turn produced (adversarial-review M7). */
    fills?: HelpFill[];
    /** This message is the retryable error bubble, not a reply. */
    error?: boolean;
    /** The student's text to resend when `error` is retried. */
    retryText?: string;
    /** Adversarial-review M5: extraction failed unretryably — `'full'` means
     *  nothing was recorded, `'partial'` means it was recorded alongside
     *  `fills`. Mirrors the main conversation's `fillNotice`. Never paired
     *  with `error` — the reply stands either way. */
    unrecordedNotice?: 'full' | 'partial';
    /** Adversarial review F7, store tracker 6.55: this reply stood on its
     *  own (never `error`), but its own fill attempt failed in a way the
     *  server guarantees is safe to re-run — mirrors the main conversation's
     *  `fillRetryText`/`FillRetryNotice`. The exact text to resend. Never
     *  paired with `unrecordedNotice` — a turn is one or the other. */
    fillRetryText?: string;
};

export const HELP_STARTERS = ['Why is this asked?', "What if I don't have it?"];

export const HELP_INVITATION = 'Ask anything about this field, or pick a starter below.';

/** Mode-aware fallback for a fill reply with no answer text of its own
 *  (defensive only — `classifyTurn`'s `'ok'` outcome guarantees a non-empty
 *  `answer`, but the fallback must not claim a direct commit in suggested
 *  mode — adversarial-review m6). */
export const HELP_FILL_FALLBACK_AUTO = 'Done — I filled that in for you.';
export const HELP_FILL_FALLBACK_SUGGESTED =
    "Got it — I've suggested that for the field. Confirm it above when you're ready.";

/** Composer-lock copy (adversarial-review B1b): the thread stays viewable —
 *  history included — but nothing more can be sent while the scoped field
 *  isn't currently extraction-eligible. */
export const HELP_LOCK_FILLED_MESSAGE = 'This field is filled — you can edit it in the form.';
export const HELP_LOCK_SUGGESTED_MESSAGE =
    "There's a suggested value waiting — Confirm or Undo it on the field.";
export const HELP_LOCK_UNAVAILABLE_MESSAGE = "This field isn't part of the form right now.";

type FieldHelpThreadProps = {
    field: CfField;
    thread: HelpMessage[];
    /** A turn is in flight for this field's thread. */
    busy: boolean;
    /** The in-flight turn has passed the slow-turn threshold. */
    slow: boolean;
    /** Returns `false` when the send-time guard refused the message
     *  (adversarial-review R6, B1c) — the composer only clears its draft on
     *  `true`, so a refused send never silently eats what the student typed.
     *  Resolves SYNCHRONOUSLY (adversarial-review N1): the draft clears the
     *  instant the send is accepted, not after the turn (1s–35s) resolves —
     *  awaiting it held the sent text on screen for the whole turn, and could
     *  wipe an unrelated draft typed afterward when the reply finally landed. */
    onSend: (text: string) => boolean;
    /** Remove the error bubble at `index` and resend its text. */
    onRetry: (index: number, text: string) => void;
    /**
     * The composer input's ref, owned and auto-focused by the HOST, not here.
     * A trap's `prev` (the control to restore focus to on close) is captured
     * by `useFocusTrap`'s own effect, and React fires a CHILD's effects
     * before its PARENT's — if this component auto-focused its own composer
     * internally, that focus move would land before the host's trap effect
     * ever captured `prev`, and the trap would "restore" focus to the
     * composer instead of the invoking Ask-AI control. Autofocus has to live
     * in the same component as the trap, declared after it (exactly how
     * `PickerSheet` orders its own trap and search-row autofocus) — so the
     * host owns the ref and the effect; this component only attaches it.
     */
    composerRef: RefObject<HTMLInputElement>;
    /**
     * The host's own Close control, so a lock that engages while the
     * composer has focus can move focus there instead of letting it fall out
     * of the dialog entirely (adversarial-review R4) — disabling a focused
     * input drops focus to the document in most browsers, which is past the
     * trap's boundary.
     */
    closeButtonRef: RefObject<HTMLButtonElement>;
    /**
     * Present when the scoped field is not currently extraction-eligible
     * (confirmed, a pending suggestion, hidden, or opted out of bot
     * collection) — the composer locks and this explains why. The thread and
     * its history stay fully visible either way (adversarial-review B1b).
     */
    lockedMessage?: string;
};

const FieldHelpThread = ({
    field,
    thread,
    busy,
    slow,
    onSend,
    onRetry,
    composerRef,
    closeButtonRef,
    lockedMessage,
}: FieldHelpThreadProps) => {
    const [draft, setDraft] = useState('');
    const logRef = useRef<HTMLDivElement>(null);
    const lockedId = `${field.id}-help-locked`;

    useEffect(() => {
        const el = logRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [thread, busy]);

    const locked = !!lockedMessage;

    // Adversarial-review R4: a lock that engages while the composer (about to
    // be disabled) or nothing meaningful has focus must not let focus escape
    // the dialog — move it to Close, which stays inside the trap. Checked
    // against both the composer directly (browsers that don't auto-blur a
    // newly-disabled input) and `document.body` (browsers/environments that do).
    useEffect(() => {
        if (!locked) return;
        const active = document.activeElement;
        if (active === composerRef.current || active === document.body || !active) {
            closeButtonRef.current?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locked]);

    const send = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || busy || locked) return;
        // Adversarial-review R6/N1: clear the draft only once the send was
        // actually accepted — `onSend` can still refuse (a race between this
        // click and the field leaving the eligible set) — but SYNCHRONOUSLY,
        // the same tick as every other composer in the widget. The turn
        // itself (1s–35s) runs detached; nothing here waits on it.
        const accepted = onSend(trimmed);
        if (accepted) setDraft('');
    };

    return (
        <div className={styles.helpBody}>
            <p className={styles.helpTitle}>{field.label}</p>
            {/* One live region for the thread (task 3.3) — the busy indicator
                below is a plain child, not its own `role="status"`: nesting a
                second live region inside this one double-announces the same
                DOM change to screen readers (adversarial-review m4). */}
            <div className={styles.helpLog} role="log" aria-live="polite" ref={logRef}>
                <div className={`${styles.helpMsg} ${styles.helpMsgBot}`}>{HELP_INVITATION}</div>
                {thread.map((message, i) =>
                    message.error ? (
                        <div key={i} className={styles.helpError} data-testid="cf-help-error">
                            <p className={styles.errorBubble}>
                                <AlertIcon />
                                <span>{message.text}</span>
                            </p>
                            {message.retryText && (
                                <button
                                    type="button"
                                    className={styles.retry}
                                    onClick={() => onRetry(i, message.retryText as string)}
                                    // Adversarial-review R1a/R5: a retry sent
                                    // while the field is locked (e.g. a
                                    // suggestion parked on it after this turn
                                    // failed) would fire with the field still
                                    // in `open_fields` and silently overwrite
                                    // the pending suggestion — `retryHelp`
                                    // itself re-guards, but disabling the
                                    // button here means the attempt is never
                                    // silently swallowed either.
                                    disabled={busy || locked}
                                >
                                    Try again
                                </button>
                            )}
                        </div>
                    ) : (
                        <div
                            key={i}
                            className={`${styles.helpMsg} ${message.role === 'user' ? styles.helpMsgUser : styles.helpMsgBot}`}
                        >
                            {/* Bot replies are markdown (the API prompt asks
                                for it) — render them through the main chat's
                                `MessageContent` (react-markdown + LinkRenderer)
                                so `**bold**`, lists and links display instead
                                of showing as literal characters. The visitor's
                                own text stays plain: they typed it, it isn't
                                markup. Error bubbles and HELP_INVITATION are
                                static copy and stay plain too. */}
                            {message.role === 'user' ? (
                                message.text
                            ) : (
                                <div className={styles.helpMarkdown} data-testid="cf-help-markdown">
                                    <MessageContent messageContent={message.text} />
                                </div>
                            )}
                            {message.unrecordedNotice && <FillNotice partial={message.unrecordedNotice === 'partial'} />}
                            {/* Adversarial review F7: the quiet fills-retry
                                affordance, scoped to this thread — mirrors the
                                main conversation's `FillRetryNotice`, reusing
                                this thread's own retry plumbing (`onRetry`)
                                rather than the main conversation's. */}
                            {message.fillRetryText && (
                                <p className={styles.fillNotice} data-testid="cf-help-fill-retry-notice">
                                    <AlertIcon size={12} />
                                    <span>{RETRYABLE_FILL_NOTICE}</span>
                                    <button
                                        type="button"
                                        className={styles.retry}
                                        onClick={() => onRetry(i, message.fillRetryText as string)}
                                        disabled={busy || locked}
                                    >
                                        Try again
                                    </button>
                                </p>
                            )}
                            {message.fills
                                ?.filter((fill) => fill.scoped)
                                .map((fill) => (
                                    <span className={styles.helpFill} key={fill.fieldId}>
                                        {fill.label}{' '}
                                        {fill.sensitive ? (
                                            <em>saved — not shown here</em>
                                        ) : (
                                            <strong>{fill.display}</strong>
                                        )}
                                    </span>
                                ))}
                            {message.fills?.some((fill) => !fill.scoped) && (
                                <ul className={styles.helpFillSecondary} aria-label="Other fields this also filled">
                                    {message.fills
                                        .filter((fill) => !fill.scoped)
                                        .map((fill) => (
                                            <li key={fill.fieldId}>
                                                {fill.label}:{' '}
                                                {fill.sensitive ? 'saved — not shown here' : fill.display}
                                            </li>
                                        ))}
                                </ul>
                            )}
                        </div>
                    )
                )}
                {busy && (
                    <div className={`${styles.helpMsg} ${styles.helpMsgBot}`}>
                        Thinking…
                        {slow && <span className={styles.slowCaption}>{SLOW_TURN_CAPTION}</span>}
                    </div>
                )}
            </div>
            {thread.length === 0 && !locked && (
                <div className={styles.helpChips}>
                    {HELP_STARTERS.map((starter) => (
                        <button
                            type="button"
                            key={starter}
                            className={styles.helpChip}
                            onClick={() => send(starter)}
                            disabled={busy}
                        >
                            {starter}
                        </button>
                    ))}
                </div>
            )}
            {locked && (
                // Adversarial-review R4: `role="status"` announces the lock
                // to AT the moment it renders — safe now that it's a sibling
                // of the log, not nested inside its own live region (m4) —
                // and `aria-describedby` on the composer ties the two
                // together for anyone tabbing straight to the (disabled)
                // input.
                <p className={styles.helpLocked} data-testid="cf-help-locked" role="status" id={lockedId}>
                    {lockedMessage}
                </p>
            )}
            <div className={styles.helpComposer}>
                <input
                    ref={composerRef}
                    className={styles.input}
                    type="text"
                    placeholder="Send a Message"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') send(draft);
                    }}
                    aria-label={`Ask about ${field.label}`}
                    aria-describedby={locked ? lockedId : undefined}
                    disabled={locked}
                />
                <button
                    type="button"
                    className={styles.helpSend}
                    onClick={() => send(draft)}
                    disabled={busy || locked || !draft.trim()}
                >
                    Send
                </button>
            </div>
        </div>
    );
};

export default FieldHelpThread;
